(() => {
  const SCROLLED_OFFSET = 16;
  const DESKTOP_DROPDOWN_CLOSE_DELAY = 180;

  function headerHasHeroAtTop(heroSelector) {
    const main = document.querySelector("#MainContent");
    if (!main) return false;

    const hero = main.querySelector(heroSelector);
    if (!hero) return false;

    const heroSection = hero.closest(".shopify-section") || hero;
    const mainTop = Math.round(main.getBoundingClientRect().top);
    const heroTop = Math.round(heroSection.getBoundingClientRect().top);

    return Math.abs(heroTop - mainTop) <= 2;
  }

  function initializeHeader(headerSection) {
    if (!headerSection || headerSection.dataset.lbHeaderInitialized === "true")
      return;
    headerSection.dataset.lbHeaderInitialized = "true";

    const header = headerSection.querySelector("[data-lb-header]");
    const mobileDrawer = headerSection.querySelector("[data-mobile-drawer]");
    const menuOpenButton = headerSection.querySelector(
      "[data-mobile-menu-open]",
    );
    const menuCloseButton = headerSection.querySelector(
      "[data-mobile-menu-close]",
    );
    const mobileMenuToggles = Array.from(
      headerSection.querySelectorAll("[data-mobile-toggle]"),
    );
    const uspSlider = headerSection.querySelector("[data-usp-slider]");
    const uspViewport = headerSection.querySelector("[data-usp-viewport]");
    const uspTrack = headerSection.querySelector("[data-usp-track]");
    const desktopNavItems = Array.from(
      headerSection.querySelectorAll(".lb-header__desktop-nav-item"),
    ).filter((item) => item.querySelector(".lb-header__desktop-dropdown"));
    const allowTransparent =
      headerSection.dataset.transparentAllowed === "true";
    const heroSelector =
      headerSection.dataset.heroSelector ||
      ".main-banner, .homepage-banner-slider";
    const announcementScrollSync =
      headerSection.dataset.announcementScrollSync !== "false";
    const mainContent = document.querySelector("#MainContent");
    const topBarContainer = document.querySelector("#top-bar-container");
    const staticUspBar = document.querySelector("[data-usp-static]");
    const topBarInitialHeight = topBarContainer
      ? Math.round(topBarContainer.getBoundingClientRect().height)
      : 0;
    const searchOpenButton = headerSection.querySelector(
      "[data-header-search-open]",
    );
    const searchPanel = headerSection.querySelector("[data-header-search-panel]");
    const searchCloseButton = headerSection.querySelector(
      "[data-header-search-close]",
    );
    const searchInput = searchPanel?.querySelector('input[name="q"]');
    const localizationOpenButton = headerSection.querySelector(
      "[data-localization-open]",
    );
    const localizationPanel = headerSection.querySelector(
      "[data-localization-panel]",
    );
    const countrySelect = headerSection.querySelector("[data-country-select]");
    const languageSelect = headerSection.querySelector("[data-language-select]");
    const mobileLocalizationOpenButton = headerSection.querySelector(
      "[data-mobile-localization-open]",
    );
    const mobileSearchOpenButtons = Array.from(
      headerSection.querySelectorAll("[data-mobile-search-open]"),
    );
    const mobileLocalizationPanel = headerSection.querySelector(
      "[data-mobile-localization-panel]",
    );
    const mobileCountrySelect = headerSection.querySelector(
      "[data-mobile-country-select]",
    );
    const mobileLanguageSelect = headerSection.querySelector(
      "[data-mobile-language-select]",
    );
    const cartTriggers = Array.from(
      headerSection.querySelectorAll("[data-header-cart-trigger]"),
    );
    const isCartPage = headerSection.dataset.isCartPage === "true";

    if (!header) return;

    if (searchPanel) searchPanel.setAttribute("aria-hidden", "true");
    if (localizationPanel) localizationPanel.setAttribute("aria-hidden", "true");
    if (mobileLocalizationPanel)
      mobileLocalizationPanel.setAttribute("aria-hidden", "true");
    header.classList.remove("is-search-open", "is-localization-open");

    const isOverlayContext =
      allowTransparent && headerHasHeroAtTop(heroSelector);

    const updateHeaderHeight = () => {
      const height = `${Math.round(headerSection.getBoundingClientRect().height)}px`;
      document.documentElement.style.setProperty("--lb-header-height", height);
    };

    const updateTopOffset = () => {
      let visibleHeight = 0;

      if (!announcementScrollSync && staticUspBar) {
        const uspRect = staticUspBar.getBoundingClientRect();
        visibleHeight += Math.max(0, Math.min(uspRect.height, uspRect.bottom));
      }

      if (topBarContainer) {
        const rect = topBarContainer.getBoundingClientRect();
        const topBarVisibleHeight = announcementScrollSync
          ? Math.max(0, Math.min(rect.height, rect.bottom))
          : Math.max(0, topBarInitialHeight);
        visibleHeight += topBarVisibleHeight;
      }

      document.documentElement.style.setProperty(
        "--lb-header-top-offset",
        `${Math.round(visibleHeight)}px`,
      );
    };

    const shouldOverlay = () =>
      isOverlayContext &&
      window.scrollY <= SCROLLED_OFFSET &&
      !header.classList.contains("is-menu-open") &&
      !header.classList.contains("is-search-open") &&
      !header.classList.contains("is-localization-open");

    const syncHeaderState = () => {
      const overlay = shouldOverlay();
      header.classList.toggle("lb-header--overlay", overlay);
      header.classList.toggle("lb-header--solid", !overlay);
      header.classList.toggle("is-scrolled", window.scrollY > SCROLLED_OFFSET);
    };

    const closeMobileMenu = () => {
      if (!mobileDrawer) return;
      mobileDrawer.classList.remove("is-open");
      header.classList.remove("is-menu-open");
      document.body.classList.remove("overflow-hidden-mobile");
      closeMobileLocalization();
      syncHeaderState();
    };

    const openMobileMenu = () => {
      if (!mobileDrawer) return;
      mobileDrawer.classList.add("is-open");
      header.classList.add("is-menu-open");
      document.body.classList.add("overflow-hidden-mobile");
      syncHeaderState();
    };

    const closeSearch = () => {
      if (!searchPanel) return;
      header.classList.remove("is-search-open");
      searchPanel.setAttribute("aria-hidden", "true");

      const predictiveSearch = searchPanel.querySelector("predictive-search");
      if (predictiveSearch?.close) predictiveSearch.close();
      syncHeaderState();
    };

    const openSearch = () => {
      if (!searchPanel) return;
      closeLocalization();
      closeMobileLocalization();
      header.classList.add("is-search-open");
      searchPanel.setAttribute("aria-hidden", "false");
      window.setTimeout(() => searchInput?.focus(), 0);
      syncHeaderState();
    };

    const closeLocalization = () => {
      if (!localizationPanel) return;
      localizationPanel.setAttribute("aria-hidden", "true");
      header.classList.remove("is-localization-open");
      syncHeaderState();
    };

    const openLocalization = () => {
      if (!localizationPanel) return;
      closeSearch();
      localizationPanel.setAttribute("aria-hidden", "false");
      header.classList.add("is-localization-open");
      syncHeaderState();
    };

    const closeMobileLocalization = () => {
      if (!mobileLocalizationPanel) return;
      mobileLocalizationPanel.setAttribute("aria-hidden", "true");
    };

    const openMobileLocalization = () => {
      if (!mobileLocalizationPanel) return;
      closeSearch();
      closeLocalization();
      mobileLocalizationPanel.setAttribute("aria-hidden", "false");
    };

    const bindLocalizationSelectors = (countryElement, languageElement) => {
      if (!countryElement || !languageElement) return () => {};

      const optionsCache = Array.from(languageElement.options).map((option) => ({
        value: option.value,
        label: option.textContent || option.label || option.value,
      }));

      const updateLanguageOptions = () => {
        const selectedCountry = countryElement.options[countryElement.selectedIndex];
        const allowedLanguageCodes = (selectedCountry?.dataset.languages || "")
          .split(",")
          .map((code) => code.trim())
          .filter(Boolean);

        const currentValue = languageElement.value;
        const sourceOptions =
          allowedLanguageCodes.length > 0
            ? optionsCache.filter((option) =>
                allowedLanguageCodes.includes(option.value),
              )
            : optionsCache;

        languageElement.innerHTML = "";
        sourceOptions.forEach((option, index) => {
          const optionElement = document.createElement("option");
          optionElement.value = option.value;
          optionElement.textContent = option.label;
          if (option.value === currentValue || (!currentValue && index === 0)) {
            optionElement.selected = true;
          }
          languageElement.appendChild(optionElement);
        });

        if (!languageElement.value && languageElement.options.length > 0) {
          languageElement.options[0].selected = true;
        }
      };

      countryElement.addEventListener("change", updateLanguageOptions);
      updateLanguageOptions();
      return updateLanguageOptions;
    };

    let uspIndex = 0;
    let uspAutoplayTimer = null;
    let uspIsDragging = false;
    let uspDragStartX = 0;
    let uspDragDelta = 0;
    let uspPointerId = null;
    const uspAutoplaySeconds = Number(
      uspSlider?.dataset.uspAutoplaySeconds || 0,
    );

    const isMobileViewport = () =>
      window.matchMedia("(max-width: 1023px)").matches;

    const getUspSlides = () =>
      uspTrack ? Array.from(uspTrack.children) : [];

    const getUspViewportWidth = () =>
      uspViewport ? uspViewport.getBoundingClientRect().width : 0;

    const applyUspTransform = (animate = true) => {
      if (!uspTrack) return;
      const basePercent = -(uspIndex * 100);

      if (uspIsDragging) {
        const viewportWidth = getUspViewportWidth();
        const dragPercent = viewportWidth
          ? (uspDragDelta / viewportWidth) * 100
          : 0;
        uspTrack.style.transition = "none";
        uspTrack.style.transform = `translateX(calc(${basePercent}% + ${dragPercent}%))`;
        return;
      }

      uspTrack.style.transition = animate
        ? "transform var(--duration-default) var(--ease-smooth)"
        : "none";
      uspTrack.style.transform = `translateX(${basePercent}%)`;
    };

    const stopUspAutoplay = () => {
      if (uspAutoplayTimer) {
        window.clearInterval(uspAutoplayTimer);
        uspAutoplayTimer = null;
      }
    };

    const startUspAutoplay = () => {
      stopUspAutoplay();

      if (!isMobileViewport() || !uspAutoplaySeconds) return;

      const slides = getUspSlides();
      if (slides.length <= 1) return;

      uspAutoplayTimer = window.setInterval(() => {
        uspIndex = (uspIndex + 1) % slides.length;
        syncUspSlider();
      }, uspAutoplaySeconds * 1000);
    };

    const syncUspSlider = () => {
      if (!uspSlider || !uspTrack) return;
      const slides = getUspSlides();
      const hasMultipleSlides = slides.length > 1;

      if (!isMobileViewport()) {
        uspIndex = 0;
        applyUspTransform(false);
        stopUspAutoplay();
        return;
      }

      if (!hasMultipleSlides) {
        uspIndex = 0;
      } else {
        uspIndex = Math.max(0, Math.min(uspIndex, slides.length - 1));
      }

      applyUspTransform();
      startUspAutoplay();
    };

    const beginUspDrag = (clientX) => {
      if (!isMobileViewport()) return;
      const slides = getUspSlides();
      if (slides.length <= 1) return;

      uspIsDragging = true;
      uspDragStartX = clientX;
      uspDragDelta = 0;
      stopUspAutoplay();
      applyUspTransform(false);
    };

    const updateUspDrag = (clientX) => {
      if (!uspIsDragging) return;
      uspDragDelta = clientX - uspDragStartX;
      applyUspTransform(false);
    };

    const completeUspDrag = () => {
      if (!uspIsDragging) return;
      const slides = getUspSlides();
      const viewportWidth = getUspViewportWidth();
      const threshold = Math.max(16, viewportWidth * 0.12);

      if (slides.length > 1) {
        if (uspDragDelta <= -threshold) {
          uspIndex = Math.min(uspIndex + 1, slides.length - 1);
        } else if (uspDragDelta >= threshold) {
          uspIndex = Math.max(uspIndex - 1, 0);
        }
      }

      uspIsDragging = false;
      uspDragStartX = 0;
      uspDragDelta = 0;
      uspPointerId = null;
      applyUspTransform();
      startUspAutoplay();
    };

    const handleUspPointerDown = (event) => {
      uspPointerId = event.pointerId;
      uspViewport?.setPointerCapture?.(event.pointerId);
      beginUspDrag(event.clientX);
    };

    const handleUspPointerMove = (event) => {
      if (uspPointerId !== null && event.pointerId !== uspPointerId) return;
      updateUspDrag(event.clientX);
    };

    const handleUspPointerUp = (event) => {
      if (uspPointerId !== null && event.pointerId !== uspPointerId) return;
      completeUspDrag();
    };

    const handleUspTouchStart = (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      beginUspDrag(touch.clientX);
    };

    const handleUspTouchMove = (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      if (uspIsDragging) event.preventDefault();
      updateUspDrag(touch.clientX);
    };

    const handleUspTouchEnd = () => {
      completeUspDrag();
    };

    const desktopDropdownTimers = new WeakMap();

    const clearDesktopDropdownTimer = (item) => {
      const timer = desktopDropdownTimers.get(item);
      if (timer) {
        window.clearTimeout(timer);
        desktopDropdownTimers.delete(item);
      }
    };

    const closeDesktopDropdown = (item) => {
      clearDesktopDropdownTimer(item);
      item.classList.remove("is-open");
    };

    const closeAllDesktopDropdowns = (exceptItem = null) => {
      desktopNavItems.forEach((item) => {
        if (exceptItem && item === exceptItem) return;
        closeDesktopDropdown(item);
      });
    };

    const openDesktopDropdown = (item) => {
      clearDesktopDropdownTimer(item);
      closeAllDesktopDropdowns(item);
      item.classList.add("is-open");
    };

    const scheduleDesktopDropdownClose = (item) => {
      clearDesktopDropdownTimer(item);
      const timer = window.setTimeout(() => {
        item.classList.remove("is-open");
        desktopDropdownTimers.delete(item);
      }, DESKTOP_DROPDOWN_CLOSE_DELAY);
      desktopDropdownTimers.set(item, timer);
    };

    const getCartUrl = () => window.routes?.cart_url || "/cart";
    const getSearchUrl = () => {
      const root = window.Shopify?.routes?.root || "/";
      return `${root}search`;
    };
    const getCartJsonUrl = () => {
      const root = window.Shopify?.routes?.root || "/";
      return `${root}cart.js`;
    };

    const openCartDrawer = (triggeredBy) => {
      const cartDrawer = document.querySelector("cart-drawer");
      if (!cartDrawer?.open) return false;
      cartDrawer.open(triggeredBy);
      return true;
    };

    const fetchCartItemCount = async () => {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        return cartDrawer.classList.contains("is-empty") ? 0 : 1;
      }

      try {
        const response = await fetch(getCartJsonUrl(), {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (!response.ok) throw new Error("Unable to fetch cart");
        const cart = await response.json();
        return Number(cart?.item_count || 0);
      } catch {
        return null;
      }
    };

    const handleCartTriggerClick = async (event) => {
      const trigger = event.currentTarget;
      if (!(trigger instanceof HTMLAnchorElement)) return;

      const hasDrawer = !!document.querySelector("cart-drawer");

      if (hasDrawer || isCartPage) {
        event.stopImmediatePropagation();
      }

      if (isCartPage) {
        event.preventDefault();
        return;
      }

      if (!hasDrawer) return;

       event.preventDefault();

      if (openCartDrawer(trigger)) return;

      window.location.assign(trigger.href || getCartUrl());

     };

    const handleMobileSearchOpen = () => {
      closeMobileMenu();
      if (searchPanel) {
        openSearch();
        return;
      }
      window.location.assign(getSearchUrl());
    };

    mobileMenuToggles.forEach((toggle) => {
      const item = toggle.closest("[data-mobile-item]");
      if (!item) return;

      if (item.dataset.initialOpen === "true") {
        item.classList.add("is-open");
      }

      toggle.addEventListener("click", () => {
        const wasOpen = item.classList.contains("is-open");
        headerSection
          .querySelectorAll("[data-mobile-item].is-open")
          .forEach((openItem) => {
            openItem.classList.remove("is-open");
          });

        if (!wasOpen) {
          item.classList.add("is-open");
        }
      });
    });

    desktopNavItems.forEach((item) => {
      item.addEventListener("mouseenter", () => {
        openDesktopDropdown(item);
      });

      item.addEventListener("mouseleave", () => {
        scheduleDesktopDropdownClose(item);
      });

      item.addEventListener("focusin", () => {
        openDesktopDropdown(item);
      });

      item.addEventListener("focusout", (event) => {
        const nextFocusTarget = event.relatedTarget;
        if (nextFocusTarget instanceof Node && item.contains(nextFocusTarget)) {
          return;
        }
        scheduleDesktopDropdownClose(item);
      });
    });

    menuOpenButton?.addEventListener("click", openMobileMenu);
    menuCloseButton?.addEventListener("click", closeMobileMenu);
    cartTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleCartTriggerClick);
    });
    searchOpenButton?.addEventListener("click", openSearch);
    searchCloseButton?.addEventListener("click", closeSearch);
    localizationOpenButton?.addEventListener("click", () => {
      if (header.classList.contains("is-localization-open")) {
        closeLocalization();
      } else {
        openLocalization();
      }
    });
    mobileLocalizationOpenButton?.addEventListener("click", () => {
      if (mobileLocalizationPanel?.getAttribute("aria-hidden") === "false") {
        closeMobileLocalization();
      } else {
        openMobileLocalization();
      }
    });
    mobileSearchOpenButtons.forEach((button) => {
      button.addEventListener("click", handleMobileSearchOpen);
    });
    uspViewport?.addEventListener("pointerdown", handleUspPointerDown);
    window.addEventListener("pointermove", handleUspPointerMove);
    window.addEventListener("pointerup", handleUspPointerUp);
    window.addEventListener("pointercancel", handleUspPointerUp);
    uspViewport?.addEventListener("touchstart", handleUspTouchStart, {
      passive: true,
    });
    uspViewport?.addEventListener("touchmove", handleUspTouchMove, {
      passive: false,
    });
    uspViewport?.addEventListener("touchend", handleUspTouchEnd, {
      passive: true,
    });
    uspViewport?.addEventListener("touchcancel", handleUspTouchEnd, {
      passive: true,
    });

    mobileDrawer?.addEventListener("click", (event) => {
      if (event.target === mobileDrawer) {
        closeMobileMenu();
      }
    });

    document.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const clickedMobileSearchTrigger = mobileSearchOpenButtons.some((button) =>
        button.contains(target),
      );

      if (
        searchPanel &&
        header.classList.contains("is-search-open") &&
        !searchPanel.contains(target) &&
        !searchOpenButton?.contains(target) &&
        !clickedMobileSearchTrigger
      ) {
        closeSearch();
      }

      if (
        localizationPanel &&
        header.classList.contains("is-localization-open") &&
        !localizationPanel.contains(target) &&
        !localizationOpenButton?.contains(target)
      ) {
        closeLocalization();
      }

      if (
        mobileLocalizationPanel &&
        mobileLocalizationPanel.getAttribute("aria-hidden") === "false" &&
        !mobileLocalizationPanel.contains(target) &&
        !mobileLocalizationOpenButton?.contains(target)
      ) {
        closeMobileLocalization();
      }

      if (
        desktopNavItems.length &&
        !desktopNavItems.some((item) => item.contains(target))
      ) {
        closeAllDesktopDropdowns();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      if (mobileDrawer?.classList.contains("is-open")) closeMobileMenu();
      if (header.classList.contains("is-search-open")) closeSearch();
      if (header.classList.contains("is-localization-open")) closeLocalization();
      if (mobileLocalizationPanel?.getAttribute("aria-hidden") === "false") {
        closeMobileLocalization();
      }
    });

    const handleScroll = () => {
      updateTopOffset();
      syncHeaderState();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", () => {
      updateTopOffset();
      updateHeaderHeight();
      syncHeaderState();
      syncUspSlider();
    });

    mainContent?.classList.toggle(
      "lb-main-with-header-offset",
      !isOverlayContext,
    );
    updateTopOffset();
    bindLocalizationSelectors(countrySelect, languageSelect);
    bindLocalizationSelectors(mobileCountrySelect, mobileLanguageSelect);
    syncUspSlider();
    updateHeaderHeight();
    syncHeaderState();
  }

  function initializeAll() {
    document
      .querySelectorAll("[data-lb-header-section]")
      .forEach(initializeHeader);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeAll);
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", initializeAll);
})();
